import { DocumentService } from '@services/DocumentService';
import { IDocumentData } from '@models/document/IDocumentData';
import { DocumentSlice } from '@/store/reducers/DocumentReducer';
import { AppDispatch } from '@/store/index';
import { AxiosResponse } from 'axios';
import { IDocumentScheme } from '@models/document/IDocumentScheme';
import { DocumentActionsService } from '@services/actions/DocumentActionsService';
import { ProcessActionsService } from '@services/actions/ProcessActionsService';
import { IActivityAction, IBaseAction } from '@models/actions/IBaseAction';
import { groupByToArray } from '@utils/helpers';
import { FavoritesService } from '@services/FavoritesService';
import { IAllRoutesActions } from '@models/actions/IAllRoutesActions';
import { IDocumentTitleBarActions } from '@models/actions/IDocumentActions';
import { RouteMapActionsService } from '@services/actions/RouteMapActionsService';
import { sendModalMsg } from '@srm/ui/lib/modalMessages';
import { IDocumentResult, IWarningAction } from '@models/document/IDocumentResult';
import { DocumentBaseActionType } from '@/types/document.types';
import { ActionAccessibleCheckResult } from '@models/actions/ActionAccessibleCheckResult';
import { ICopyAndEditAttachesAddResponse } from '@models/attaches/ICopyAndEditAttaches';
import { CopyAndEditAttachesService } from '@services/CopyAndEditAttachesService';
import { isRequestCanceled } from '@srm/core/http';
import { errorTextWithTraceId } from '@srm/core/errors';
import { getCurrentOpenToken, isCurrentOpen } from './documentOpenGuid';

let currentDocumentDataController: AbortController | undefined;
let currentDocumentSchemeController: AbortController | undefined;
// «Последний победил» для titleBarActions. В отличие от favorite/статус-бара (один фетч на открытие,
// гард по guid открытия), titleBarActions может стартовать НЕСКОЛЬКО раз в ОДНОМ открытии (прямой
// T=0-фетч vs coalesced refresh по catchup-ревизии) — guid у них одинаков и порядок не выражает.
// Поэтому здесь свой per-request seq: набор пишется в одно не-keyed redux-поле, сервисы действий не
// принимают AbortSignal, и токен отбрасывает диспатч любого ответа, кроме самого позднего.
let titleBarActionsRequestSeq = 0;
let menuDocumentActionsRequestSeq = 0;
let menuAllRoutesActionsRequestSeq = 0;

export type MenuActionsFetchResult = 'applied' | 'superseded' | 'open-changed' | 'failed';

const isRequestCancelled = (error: unknown, controller: AbortController): boolean => {
    return isRequestCanceled(error, controller.signal);
};

const abortDocumentDataRequest = () => {
    currentDocumentDataController?.abort();
    currentDocumentDataController = undefined;
};

const abortDocumentSchemeRequest = () => {
    currentDocumentSchemeController?.abort();
    currentDocumentSchemeController = undefined;
};

/** Возвращает загруженные данные; undefined — запрос отменён, перебит более новым или завершился ошибкой. */
const fetchDocumentDataInternal = async (
    dispatch: AppDispatch,
    getDataFunc: (signal: AbortSignal) => Promise<AxiosResponse<IDocumentData>>,
    errorMode: 'modal' | 'page' = 'modal',
): Promise<IDocumentData | undefined> => {
    abortDocumentDataRequest();
    const controller = new AbortController();
    currentDocumentDataController = controller;

    try {
        dispatch(DocumentSlice.actions.setIsLoading(true));
        const response = await getDataFunc(controller.signal);
        if (currentDocumentDataController !== controller) return undefined;
        dispatch(DocumentSlice.actions.successLoadData(response.data));
        return response.data;
    } catch (e) {
        if (isRequestCancelled(e, controller)) return undefined;
        if (currentDocumentDataController !== controller) return undefined;
        if (errorMode === 'page') {
            dispatch(
                DocumentSlice.actions.setLoadError(
                    errorTextWithTraceId(e, 'Не удалось загрузить данные документа. Повторите попытку.'),
                ),
            );
        } else {
            let error = e as string[];
            sendModalMsg({
                message: error,
                type: 'error',
            });
        }
        return undefined;
    } finally {
        if (currentDocumentDataController === controller) {
            currentDocumentDataController = undefined;
            dispatch(DocumentSlice.actions.setIsLoading(false));
        }
    }
};

const fetchDocumentSchemeInternal = async (
    dispatch: AppDispatch,
    getSchemeFunc: (signal: AbortSignal) => Promise<AxiosResponse<IDocumentScheme>>,
) => {
    abortDocumentSchemeRequest();
    const controller = new AbortController();
    currentDocumentSchemeController = controller;

    try {
        dispatch(DocumentSlice.actions.setIsEdit(false));
        dispatch(DocumentSlice.actions.setIsLoading(true));
        const response = await getSchemeFunc(controller.signal);
        if (currentDocumentSchemeController !== controller) return;
        dispatch(DocumentSlice.actions.successLoadScheme(response.data));
    } catch (e) {
        if (isRequestCancelled(e, controller)) return;
        if (currentDocumentSchemeController !== controller) return;
        dispatch(
            DocumentSlice.actions.setLoadError(
                errorTextWithTraceId(e, 'Не удалось загрузить форму документа. Повторите попытку.'),
            ),
        );
    } finally {
        if (currentDocumentSchemeController === controller) {
            currentDocumentSchemeController = undefined;
            dispatch(DocumentSlice.actions.setIsLoading(false));
        }
    }
};

export interface ISaveDocumentDataOptions {
    skipReloadAfterSave?: boolean;
    keepLoadingOnSuccess?: boolean;
    /**
     * Применяет перезагруженный документ. Смена данных, выход из правки и снятие загрузки
     * идут одной пачкой — карточка может провести их одной склейкой (смена режима).
     */
    applyReloaded?: (apply: () => void) => Promise<void>;
}

export const DocumentActionCreators = {
    fetchDocumentScheme: (id: string, openDocumentGuid: string | undefined) => async (dispatch: AppDispatch) => {
        await fetchDocumentSchemeInternal(dispatch, (signal) => {
            return DocumentService.getScheme(id, undefined, openDocumentGuid, signal, true);
        });
    },

    /**
     * Ленивая загрузка контент-моделей для технических инспекторов формы.
     */
    fetchContentModels: (id: string) => async (dispatch: AppDispatch) => {
        try {
            dispatch(DocumentSlice.actions.startLoadContentModels(id));
            const response = await DocumentService.getContentModels(id);
            dispatch(DocumentSlice.actions.successLoadContentModels({ docId: id, contentModels: response.data }));
            return true;
        } catch (e) {
            // Тихий фейл: дебаг-инспектор не блокирует работу с документом
            // eslint-disable-next-line no-console
            console.warn('[DocumentInspector] Не удалось загрузить contentModels:', e);
            return false;
        } finally {
            dispatch(DocumentSlice.actions.finishLoadContentModels(id));
        }
    },

    fetchNewDocumentCopyAttachesScheme: (flowName: string) => async (dispatch: AppDispatch) => {
        try {
            dispatch(DocumentSlice.actions.setIsLoading(true));
            const response = await CopyAndEditAttachesService.fetchScheme(flowName);
            dispatch(DocumentSlice.actions.successLoadNewDocCopyAttachesScheme(response.data));
        } catch (e) {
            let error = e as string[];
            sendModalMsg({
                message: error,
                type: 'error',
            });
        } finally {
            dispatch(DocumentSlice.actions.setIsLoading(false));
        }
    },

    fetchNewDocumentCopyAttachesData: (baseDocId: string, flowName: string) => async (dispatch: AppDispatch) => {
        try {
            dispatch(DocumentSlice.actions.setIsLoading(true));
            const response = await CopyAndEditAttachesService.fetchData('-1', baseDocId, flowName);
            dispatch(DocumentSlice.actions.successLoadNewDocCopyAttachesData(response.data));

            let initSelected = response.data?.data
                .filter((file) => file.isSelected)
                .map((data) => {
                    return {
                        key: data.key,
                        fields: data.fields,
                    } as ICopyAndEditAttachesAddResponse;
                });

            dispatch(DocumentSlice.actions.setPartsVersionToCopy(initSelected));
        } catch (e) {
            let error = e as string[];
            sendModalMsg({
                message: error,
                type: 'error',
            });
        } finally {
            dispatch(DocumentSlice.actions.setIsLoading(false));
        }
    },

    fetchDocumentSchemeByAction: (actId: string, baseDocId?: string) => async (dispatch: AppDispatch) => {
        await fetchDocumentSchemeInternal(dispatch, (signal) => {
            return DocumentService.getSchemeByAction(actId, baseDocId, undefined, signal, true);
        });
    },

    fetchDocumentData:
        (id: string, openDocumentGuid: string | undefined, options?: { errorMode?: 'modal' | 'page' }) =>
        async (dispatch: AppDispatch) =>
            fetchDocumentDataInternal(
                dispatch,
                (signal) => {
                    // deferLinks: содержимое таблиц-линков карточка догружает параллельным запросом
                    // (useDocumentLinksLoad) — сюда приходят заглушки.
                    return DocumentService.getData(id, openDocumentGuid, signal, true, options?.errorMode === 'page');
                },
                options?.errorMode,
            ),

    setDocumentData: (data: IDocumentData) => async (dispatch: AppDispatch) => {
        abortDocumentDataRequest();
        try {
            dispatch(DocumentSlice.actions.setIsLoading(true));
            dispatch(DocumentSlice.actions.successLoadData(data));
        } catch (e) {
            let error = e as string[];
            sendModalMsg({
                message: error,
                type: 'error',
            });
        } finally {
            dispatch(DocumentSlice.actions.setIsLoading(false));
        }
    },
    fetchDocumentDataByAction:
        (actId: string, docId?: string, templateId?: string) => async (dispatch: AppDispatch) => {
            await fetchDocumentDataInternal(
                dispatch,
                (signal) => {
                    return DocumentService.getDataByAction(
                        actId,
                        docId,
                        templateId,
                        undefined,
                        undefined,
                        signal,
                        true,
                    );
                },
                'page',
            );
        },
    fetchDocumentStatusBar: (id: string, openDocumentGuid: string | undefined) => async (dispatch: AppDispatch) => {
        // Гард по ТОКЕНУ открытия (не по guid-параметру: guid короткоживущий и для нового дока/late-
        // запроса может быть undefined — тогда isCurrentOpen(undefined) ложно бэйлил бы легитимную
        // запись и оставлял спиннер). Токен живёт всё открытие. openDocumentGuid — только backend-параметр.
        const openToken = getCurrentOpenToken();
        const abortController = new AbortController();
        try {
            dispatch(DocumentSlice.actions.setIsLoadingStatusBar(true));
            const response = await DocumentService.getStatusBar(abortController, id, openDocumentGuid);
            if (!isCurrentOpen(openToken)) return abortController; // сменилось открытие — не перетираем статус-бар другого документа
            dispatch(DocumentSlice.actions.successLoadStatusBar(response.data));
        } catch (e) {
            if (!isCurrentOpen(openToken)) return abortController;
            let error = e as string[];
            sendModalMsg({
                message: error,
                type: 'error',
            });
        } finally {
            if (isCurrentOpen(openToken)) {
                dispatch(DocumentSlice.actions.setIsLoadingStatusBar(false));
            }
        }

        return abortController;
    },
    fetchDocumentTitleBarActions:
        (id: string, openDocumentGuid: string | undefined, options?: { silent?: boolean }) =>
        async (dispatch: AppDispatch) => {
            const seq = ++titleBarActionsRequestSeq;
            // silent (coalesced refresh): НЕ поднимаем флаг загрузки — кнопки заголовка остаются на
            // месте (fetch-then-swap, без «моргания»). Видимый спиннер нужен лишь холодному T=0-фетчу,
            // когда кнопок ещё нет.
            const silent = options?.silent === true;
            try {
                if (!silent) dispatch(DocumentSlice.actions.setIsLoadingTitleBarActions(true));
                const documentActionsService = new DocumentActionsService();
                const processActionsService = new ProcessActionsService();
                // Параллельно: под-запросы независимы — половина задержки против последовательного await.
                // Promise.all: при отказе любого падаем в catch без частичного (полу-устаревшего) набора.
                const [responseDocActions, responseAllActActions] = await Promise.all([
                    documentActionsService.fetchActionsDocumentTitleBar(id, openDocumentGuid),
                    processActionsService.fetchAllRouteActionsTitleBar(id, openDocumentGuid),
                ]);

                let resultActions: IDocumentTitleBarActions = {};
                resultActions.documentActions = [];
                resultActions.groupedActivityActions = [];
                resultActions.processActions = [];

                //действия по документу
                resultActions.documentActions = responseDocActions.data;

                //действия по активити
                let activityActions: { activityKey: string; action: IActivityAction }[] = [];
                for (let activityKey in responseAllActActions.data.activityActions) {
                    let actions = responseAllActActions.data.activityActions[activityKey];
                    activityActions = activityActions.concat(
                        actions.map((action: IActivityAction) => ({
                            activityKey: activityKey,
                            action: action,
                        })),
                    );
                }

                resultActions.groupedActivityActions = groupByToArray(
                    activityActions.map((activityAction: { activityKey: string; action: IActivityAction }) => ({
                        ...activityAction.action,
                        activityKey: activityAction.activityKey,
                    })),
                    (activityAction: any) => activityAction.key,
                ) as { key: string; value: (IActivityAction & { activityKey: string })[] }[];

                //действия по процессам
                for (let processKey in responseAllActActions.data.processActions) {
                    let actions = responseAllActActions.data.processActions[processKey];
                    resultActions.processActions = resultActions.processActions.concat(
                        actions.map((action: IBaseAction) => ({
                            processKey: processKey,
                            action: action,
                        })),
                    );
                }

                if (seq !== titleBarActionsRequestSeq) return; // устарел — не перетираем свежий набор
                dispatch(DocumentSlice.actions.successLoadTitleBarActions(resultActions));
            } catch (e) {
                if (seq !== titleBarActionsRequestSeq) return; // ошибка устаревшего запроса не для текущего документа
                // silent (фоновое обновление по ревизии/reload/save): НЕ показываем модалку (не пугаем
                // на фоне) и пробрасываем ошибку вызывающему — useDocumentActionsRevision НЕ поднимет
                // гейт ревизии (markKnownRevision) на провале, поэтому повторная доставка той же
                // ревизии (reconnect/catchup) перечитает набор, а не залипнет на устаревшем.
                if (silent) throw e;
                let error = e as string[];
                sendModalMsg({
                    message: error,
                    type: 'error',
                });
            } finally {
                // Снимаем флаг загрузки, только если мы — последний запрос (иначе оставим спиннер для
                // ещё летящего свежего). Снимаем и для silent: он мог вытеснить не-silent T=0, чей флаг
                // иначе завис бы в true.
                if (seq === titleBarActionsRequestSeq) {
                    dispatch(DocumentSlice.actions.setIsLoadingTitleBarActions(false));
                }
            }
        },
    fetchDocumentMenuActions:
        (id: string, options?: { silent?: boolean }) => async (dispatch: AppDispatch) => {
            const seq = ++menuDocumentActionsRequestSeq;
            const openToken = getCurrentOpenToken();
            const silent = options?.silent === true;
            try {
                if (!silent) dispatch(DocumentSlice.actions.setIsLoadingMenuDocumentActions(true));

                const documentActionsService = new DocumentActionsService();
                const responseDocActions = await documentActionsService.fetchActionsDocumentView(id);

                if (seq !== menuDocumentActionsRequestSeq) return 'superseded' as const;
                if (!isCurrentOpen(openToken)) return 'open-changed' as const;
                dispatch(DocumentSlice.actions.successLoadMenuDocumentActions(responseDocActions.data));
                return 'applied' as const;
            } catch (e) {
                if (seq !== menuDocumentActionsRequestSeq) return 'superseded' as const;
                if (!isCurrentOpen(openToken)) return 'open-changed' as const;
                // При тихой перепроверке оставляем последний успешный набор и не показываем
                // пользователю ошибку фонового запроса.
                if (silent) return 'failed' as const;
                const error = e as string[];
                sendModalMsg({
                    message: error,
                    type: 'error',
                });
                return 'failed' as const;
            } finally {
                // seq достаточно: если начался более новый запрос, его loading-флаг не снимаем.
                // При смене документа без нового запроса старый loading, наоборот, нужно погасить.
                if (seq === menuDocumentActionsRequestSeq) {
                    dispatch(DocumentSlice.actions.setIsLoadingMenuDocumentActions(false));
                }
            }
        },
    fetchDocumentMenuAllRoutesActions:
        (id: string, options?: { silent?: boolean }) => async (dispatch: AppDispatch) => {
            const seq = ++menuAllRoutesActionsRequestSeq;
            const openToken = getCurrentOpenToken();
            const silent = options?.silent === true;
            try {
                if (!silent) dispatch(DocumentSlice.actions.setIsLoadingMenuAllRoutesActions(true));

                const processActionsService = new ProcessActionsService();
                const responseAllActActions = await processActionsService.fetchActionsDocumentView(id);

                const resultActions: IAllRoutesActions = {};

                //действия по активити
                resultActions.activityActions = responseAllActActions.data.activityActions;

                //действия по процессам
                resultActions.processActions = responseAllActActions.data.processActions;

                if (seq !== menuAllRoutesActionsRequestSeq) return 'superseded' as const;
                if (!isCurrentOpen(openToken)) return 'open-changed' as const;
                dispatch(DocumentSlice.actions.successLoadMenuAllRoutesActions(resultActions));
                return 'applied' as const;
            } catch (e) {
                if (seq !== menuAllRoutesActionsRequestSeq) return 'superseded' as const;
                if (!isCurrentOpen(openToken)) return 'open-changed' as const;
                if (silent) return 'failed' as const;
                const error = e as string[];
                sendModalMsg({
                    message: error,
                    type: 'error',
                });
                return 'failed' as const;
            } finally {
                if (seq === menuAllRoutesActionsRequestSeq) {
                    dispatch(DocumentSlice.actions.setIsLoadingMenuAllRoutesActions(false));
                }
            }
        },
    fetchDocumentIsFavorite: (id: string, options?: { silent?: boolean }) => async (dispatch: AppDispatch) => {
        // Гард по токену открытия: вызывается синхронно из resetAndLoadDocInfo (сразу после
        // beginDocumentOpen), поэтому захват текущего токена = это открытие.
        const openToken = getCurrentOpenToken();
        try {
            // При фоновом refresh сохраняем последнее значение до ответа, чтобы звезда
            // избранного не мигала поверх оставленной на экране карточки.
            if (!options?.silent) dispatch(DocumentSlice.actions.setIsFavorite(false));
            const response = await FavoritesService.checkIsFavoriteDocument(id);
            if (!isCurrentOpen(openToken)) return; // сменилось открытие — не перетираем звезду другого документа
            dispatch(DocumentSlice.actions.setIsFavorite(response.data));
        } catch (e) {
            if (!isCurrentOpen(openToken)) return;
            console.log(e);
            let error = e as string[];
            sendModalMsg({
                message: error,
                type: 'error',
            });
        }
    },
    fetchRouteMapAddActionConfig: (id: string) => async (dispatch: AppDispatch) => {
        try {
            const response = await RouteMapActionsService.fetchActionConfig(id, 'AddAction');
            dispatch(DocumentSlice.actions.setRouteMapAddActionConfig(response.data));
        } catch (e) {
            console.log(e);
            let error = e as string[];
            sendModalMsg({
                message: error,
                type: 'error',
            });
        }
    },
    addDocumentToFavorites: (id: string) => async (dispatch: AppDispatch) => {
        try {
            await FavoritesService.addDocumentToFavorites(id);
            dispatch(DocumentSlice.actions.setIsFavorite(true));
        } catch (e) {
            console.log(e);
            let error = e as string[];
            sendModalMsg({
                message: error,
                type: 'error',
            });
        }
    },
    removeDocumentFromFavorites: (id: string) => async (dispatch: AppDispatch) => {
        try {
            await FavoritesService.removeDocumentFromFavorites(id);
            dispatch(DocumentSlice.actions.setIsFavorite(false));
        } catch (e) {
            console.log(e);
            let error = e as string[];
            sendModalMsg({
                message: error,
                type: 'error',
            });
        }
    },
    setDocumentIsLoading: (flag: boolean) => async (dispatch: AppDispatch) => {
        dispatch(DocumentSlice.actions.setIsLoading(flag));
    },
    setDocumentIsEdit: (flag: boolean) => async (dispatch: AppDispatch) => {
        dispatch(DocumentSlice.actions.setIsEdit(flag));
    },

    setDocumentIsNew: (flag: boolean) => async (dispatch: AppDispatch) => {
        dispatch(DocumentSlice.actions.setIsNew(flag));
    },
    resetDocument:
        (options?: { keepTitleBarActions?: boolean; keepContent?: boolean }) => async (dispatch: AppDispatch) => {
            abortDocumentDataRequest();
            abortDocumentSchemeRequest();
            dispatch(DocumentSlice.actions.reset(options));
        },
    resetDocumentActions: () => async (dispatch: AppDispatch) => {
        dispatch(DocumentSlice.actions.resetActions());
    },
    setDocumentIsHistoryView: (flag: boolean) => async (dispatch: AppDispatch) => {
        dispatch(DocumentSlice.actions.setIsHistoryView(flag));
    },
    setPartsVersionToAttach: (partsVersions: string[]) => async (dispatch: AppDispatch) => {
        dispatch(DocumentSlice.actions.setPartsVersionToAttach(partsVersions));
    },
    setPartsVersionToCopy: (partsVesrions: ICopyAndEditAttachesAddResponse[]) => async (dispatch: AppDispatch) => {
        dispatch(DocumentSlice.actions.setPartsVersionToCopy(partsVesrions));
    },
    setActionWarning: (action?: IWarningAction) => async (dispatch: AppDispatch) => {
        dispatch(DocumentSlice.actions.setActionWarning(action));
    },
    saveDocumentData:
        (
            data: IDocumentData,
            templateId?: string,
            options?: ISaveDocumentDataOptions,
        ) =>
        async (dispatch: AppDispatch): Promise<IDocumentResult | undefined> => {
            let keepLoadingOnSuccess = false;

            try {
                dispatch(DocumentSlice.actions.setIsLoading(true));
                dispatch(DocumentSlice.actions.setActionWarning(undefined));
                const result = await DocumentService.saveData(data, templateId);

                if (result.data.warningAction) {
                    dispatch(DocumentSlice.actions.setActionWarning(result.data.warningAction));
                    return undefined;
                }

                if (result.data.warnings && result.data.warnings.length > 0) {
                    sendModalMsg({
                        message: result.data.warnings,
                        type: 'warning',
                    });
                }

                if (options?.skipReloadAfterSave) {
                    keepLoadingOnSuccess = Boolean(options.keepLoadingOnSuccess);
                    return result.data;
                }

                // deferLinks: перезагрузку после сохранения ждёт та же карточка — содержимое
                // таблиц-линков она догрузит по новому payload (useDocumentLinksLoad).
                const response = await DocumentService.getData(result.data.id, undefined, undefined, true);
                const apply = () => {
                    dispatch(DocumentSlice.actions.successLoadData(response.data));
                    dispatch(DocumentSlice.actions.setIsEdit(false));
                    dispatch(DocumentSlice.actions.setIsLoading(false));
                };
                if (options?.applyReloaded) {
                    await options.applyReloaded(apply);
                } else {
                    apply();
                }

                return result.data;
            } catch (e) {
                console.log(e);
                let error = e as string[];
                sendModalMsg({
                    title: 'Документ не сохранён',
                    message: error,
                    type: 'error',
                });

                return undefined;
            } finally {
                if (!keepLoadingOnSuccess) {
                    dispatch(DocumentSlice.actions.setIsLoading(false));
                }
            }
        },
    checkDocumentBaseActionAccessibility:
        (
            docId: string,
            actionKey: DocumentBaseActionType,
            onAccessibleAction: (result: ActionAccessibleCheckResult) => void,
            onInaccessibleAction: (result: ActionAccessibleCheckResult) => void,
            /** Проверка не удалась; ошибку уже показали */
            onCheckFailed?: () => void,
        ) =>
        async (_dispatch: AppDispatch) => {
            const svc = new DocumentActionsService();
            svc.checkBaseActionAccessibility(docId, actionKey)
                .then(async (res) => {
                    const result = res.data;
                    if (result.isAccessibly) {
                        //успешная проверка Accessibility
                        onAccessibleAction(result);
                    } else {
                        onInaccessibleAction(result);
                    }
                })
                .catch((reason) => {
                    console.log('Ошибка проверки доступности действия: ' + reason);
                    sendModalMsg({
                        title: 'Ошибка проверки доступности действия',
                        message: reason,
                        type: 'error',
                    });
                    onCheckFailed?.();
                });
        },
};
